/**
 * Redux slice (src/store/slices/teacherSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  AddNewTeacher,
  DeleteTeacher,
  fetch5Teachers,
  fetchTrialTeacher,
  fetchTeacherBookings,
  fetchTeacherDetails,
  fetchTeacherDetailsWithoutSlot,
  GetExistingTeacher_Availability,
  GetTeachers,
  setTeacherArchiveStatus,
  imageUpload,
  videoUpload,
  SearchTeacherbyUsername,
  Signup_Teacher_By_Admin,
  Update_Teacher_By_Availability,
  updateTeacher,
  changeTeacherPassword,
  listLocalFiles,
  getMaterialsTeacher,
  uploadToLocal,
  deleteLocalFile,
  downloadLocalFile,
  updateTeacherDetails,
} from "../actions/teachersActions";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";

let initialState = {
  Teacherslist: [],
  TeacherDetails: null,
  AllTeacherlist: [],
  Teacher_Availabile_Slots: [],
  Teachers_Bookings_Status: [],
  loading: false,
  error: null,
  publicCatalogueLoading: false,
  publicCatalogueError: null,
  publicCataloguePagination: null,
  publicCatalogueRequestId: null,
  localFiles: {},
  fileLoading: false,
  fileError: null,
  materialScope: null,
  materialListRequests: {},
  materialDownloadRequests: {},
  // Optimized materials state
  materialsData: [],
  materialsLoading: false,
  materialsError: null,
  materialsPagination: null,
  materialsArchiveScope: 'active',
  downloadLoading: false,
  downloadError: null,
  currentArchiveScope: 'active',
  archiveMutationLoadingById: {},
  archiveMutationError: null,
  // Trial teacher (for direct booking from landing)
  trialTeacher: null,
  trialTeacherLoading: false,
  trialTeacherError: null,
  trialTeacherRequestId: null,
};

const upsertEntityById = (list = [], entity) => {
  if (!Array.isArray(list)) return entity ? [entity] : [];
  if (!entity?._id) return list;

  const index = list.findIndex((item) => item?._id === entity._id);
  if (index === -1) {
    return [...list, entity];
  }

  return [
    ...list.slice(0, index),
    { ...list[index], ...entity },
    ...list.slice(index + 1),
  ];
};

const removeEntityById = (list = [], entityId) =>
  Array.isArray(list) ? list.filter((item) => item?._id !== entityId) : [];

const clearMaterialRequests = (state, scope = null) => {
  state.materialScope = scope;
  state.materialListRequests = {};
  state.materialDownloadRequests = {};
  state.localFiles = {};
  state.fileLoading = false;
  state.fileError = null;
  state.downloadLoading = false;
  state.downloadError = null;
};

const enterMaterialScope = (state, action) => {
  if (action.meta?.materialScope !== undefined && state.materialScope !== action.meta.materialScope) {
    clearMaterialRequests(state, action.meta.materialScope);
  }
};

/**
 * RTK slice definition: teacherSlice.
 */
const teacherSlice = createSlice({
  name: "teacher",
  initialState: initialState,
  reducers: {
    retireMaterialScope: (state, action) => {
      if (state.materialScope === action.payload) clearMaterialRequests(state);
    },
    websocketConnected: (state) => {
      state.websocketConnected = true;
    },
    websocketDisconnected: (state) => {
      state.websocketConnected = false;
    },
    websocketMessageReceived: (state, action) => {
      state.lastWebsocketMessage = action.payload;
    },
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: fetch5Teachers.pending
      .addCase(fetch5Teachers.pending, (state, action) => {
        state.publicCatalogueRequestId = action.meta?.requestId || null;
        state.publicCatalogueLoading = true;
        state.publicCatalogueError = null;
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetch5Teachers.fulfilled
      .addCase(fetch5Teachers.fulfilled, (state, action) => {
        if (action.meta?.requestId && state.publicCatalogueRequestId !== action.meta.requestId) return;
        state.Teacherslist = action.payload;
        state.publicCataloguePagination = action.meta?.pagination || null;
        state.publicCatalogueLoading = false;
        state.publicCatalogueError = null;
        state.loading = false;
        state.error = null;
      })
      // extraReducers handler: fetch5Teachers.rejected
      .addCase(fetch5Teachers.rejected, (state, action) => {
        if (action.meta?.requestId && state.publicCatalogueRequestId !== action.meta.requestId) return;
        const message = action.payload || action.error?.message || 'Teacher catalogue unavailable';
        state.publicCatalogueLoading = false;
        state.publicCatalogueError = message;
        state.loading = false;
        state.error = message;
      })
      // extraReducers handler: fetchTrialTeacher
      .addCase(fetchTrialTeacher.pending, (state, action) => {
        state.trialTeacherRequestId = action.meta?.requestId || null;
        state.trialTeacher = null;
        state.trialTeacherMap = {};
        state.trialTeacherLoading = true;
        state.trialTeacherError = null;
      })
      .addCase(fetchTrialTeacher.fulfilled, (state, action) => {
        if (action.meta?.requestId && state.trialTeacherRequestId !== action.meta.requestId) return;
        state.trialTeacher = action.payload?.teacher || (action.payload?._id ? action.payload : null);
        state.trialTeacherMap = action.payload?.teacherMap || {};
        state.trialTeacherLoading = false;
        state.trialTeacherError = null;
      })
      .addCase(fetchTrialTeacher.rejected, (state, action) => {
        if (action.meta?.requestId && state.trialTeacherRequestId !== action.meta.requestId) return;
        state.trialTeacher = null;
        state.trialTeacherMap = {};
        state.trialTeacherLoading = false;
        state.trialTeacherError = action.payload || action.error?.message || 'Failed';
      })
      // extraReducers handler: fetchTeacherDetails.pending
      .addCase(fetchTeacherDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.currentTeacher = null;
        state.TeacherDetails = null;
      })
      // extraReducers handler: fetchTeacherDetails.fulfilled
      .addCase(fetchTeacherDetails.fulfilled, (state, action) => {
        state.currentTeacher = action.payload;
        state.TeacherDetails = action.payload;
        state.loading = false;
        state.error = null;
      })
      // extraReducers handler: fetchTeacherDetails.rejected
      .addCase(fetchTeacherDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentTeacher = null;
        state.TeacherDetails = null;
      })
      
      // extraReducers handler: fetchTeacherDetailsWithoutSlot.pending
      .addCase(fetchTeacherDetailsWithoutSlot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetchTeacherDetailsWithoutSlot.fulfilled
      .addCase(fetchTeacherDetailsWithoutSlot.fulfilled, (state, action) => {
        state.currentTeacher = action.payload;
        state.TeacherDetails = action.payload;
        state.loading = false;
        state.error = null;
      })
      // extraReducers handler: fetchTeacherDetailsWithoutSlot.rejected
      .addCase(fetchTeacherDetailsWithoutSlot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // extraReducers handler: GetTeachers.pending
      .addCase(GetTeachers.pending, (state, action) => {
        state.loading = true;
        state.currentArchiveScope = action.meta?.arg?.archiveScope || 'active';
      })
      // extraReducers handler: GetTeachers.fulfilled
      .addCase(GetTeachers.fulfilled, (state, action) => {
        state.Teacherslist = action.payload;
        state.AllTeacherlist = action.payload;
        state.loading = false;
        state.currentArchiveScope = action.meta?.arg?.archiveScope || 'active';
      })
      // extraReducers handler: GetTeachers.rejected
      .addCase(GetTeachers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(setTeacherArchiveStatus.pending, (state, action) => {
        const teacherId = action.meta?.arg?.teacherId;
        if (teacherId) {
          state.archiveMutationLoadingById[teacherId] = true;
        }
        state.archiveMutationError = null;
      })
      .addCase(setTeacherArchiveStatus.fulfilled, (state, action) => {
        const { teacherId, isArchived, teacher } = action.payload || {};
        if (teacherId) {
          delete state.archiveMutationLoadingById[teacherId];
        }

        if (teacher?._id) {
          state.AllTeacherlist = upsertEntityById(state.AllTeacherlist, teacher);
        }

        if (state.currentArchiveScope === 'all' && teacher?._id) {
          state.Teacherslist = upsertEntityById(state.Teacherslist, teacher);
          return;
        }

        const shouldRemoveFromVisibleList =
          (state.currentArchiveScope === 'active' && isArchived) ||
          (state.currentArchiveScope === 'archived' && !isArchived);

        if (shouldRemoveFromVisibleList) {
          state.Teacherslist = removeEntityById(state.Teacherslist, teacherId);
          state.AllTeacherlist = removeEntityById(state.AllTeacherlist, teacherId);
          return;
        }

        if (teacher?._id) {
          state.Teacherslist = upsertEntityById(state.Teacherslist, teacher);
        }
      })
      .addCase(setTeacherArchiveStatus.rejected, (state, action) => {
        const teacherId = action.payload?.teacherId || action.meta?.arg?.teacherId;
        if (teacherId) {
          delete state.archiveMutationLoadingById[teacherId];
        }
        state.archiveMutationError =
          action.payload?.message || action.error?.message || 'Failed to update teacher archive status';
      })
      // extraReducers handler: AddNewTeacher.pending
      .addCase(AddNewTeacher.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: AddNewTeacher.fulfilled
      .addCase(AddNewTeacher.fulfilled, (state, action) => {
        state.Teacherslist = action.payload;
        state.AllTeacherlist.push(action.payload);
        state.loading = false;
      })
      // extraReducers handler: AddNewTeacher.rejected
      .addCase(AddNewTeacher.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: imageUpload.fulfilled
      .addCase(imageUpload.fulfilled, (state, action) => {
        state.loading = false;
      })
      // extraReducers handler: imageUpload.rejected
      .addCase(imageUpload.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: DeleteTeacher.fulfilled
      .addCase(DeleteTeacher.fulfilled, (state, action) => {
        state.loading = false;
        state.AllTeacherlist = state.AllTeacherlist.filter(
          (teacher) => teacher.id !== action.payload && teacher._id !== action.payload
        );
      })
      // extraReducers handler: DeleteTeacher.rejected
      .addCase(DeleteTeacher.rejected, (state, action) => {
        state.loading = false;
      })
      // extraReducers handler: updateTeacher.fulfilled
      .addCase(updateTeacher.fulfilled, (state, action) => {
        const { teacherId, updatedTeacher } = action.payload;
        const updatedTeacherIndex = state.AllTeacherlist.findIndex(
          (teacher) => teacher._id === teacherId
        );

        if (updatedTeacherIndex !== -1) {
          const updateTeacher = {
            ...state.AllTeacherlist[updatedTeacherIndex],
            ...updatedTeacher,
          };
          state.AllTeacherlist = [
            ...state.AllTeacherlist.slice(0, updatedTeacherIndex),
            updateTeacher,
            ...state.AllTeacherlist.slice(updatedTeacherIndex + 1),
          ];
        }
        if (state.currentTeacher && state.currentTeacher._id === teacherId) {
          state.currentTeacher = { ...state.currentTeacher, ...updatedTeacher };
        }
      })
      // extraReducers handler: updateTeacher.rejected
      .addCase(updateTeacher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // extraReducers handler: Update_Teacher_By_Availability.fulfilled
      .addCase(Update_Teacher_By_Availability.fulfilled, (state, action) => {
        const { teacherId, updatedData } = action.payload;
        const updatedTeacherIndex = state.AllTeacherlist.findIndex(
          (teacher) => teacher._id === teacherId
        );

        if (updatedTeacherIndex !== -1) {
          const updatedTeacher = {
            ...state.AllTeacherlist[updatedTeacherIndex],
            ...updatedData,
          };
          state.AllTeacherlist = [
            ...state.AllTeacherlist.slice(0, updatedTeacherIndex),
            updatedTeacher,
            ...state.AllTeacherlist.slice(updatedTeacherIndex + 1),
          ];
        }
        if (state.currentTeacher && state.currentTeacher._id === teacherId) {
          state.currentTeacher = { ...state.currentTeacher, ...updatedData };
        }
      })
      // extraReducers handler: Update_Teacher_By_Availability.rejected
      .addCase(Update_Teacher_By_Availability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // extraReducers handler: Signup_Teacher_By_Admin.pending
      .addCase(Signup_Teacher_By_Admin.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: Signup_Teacher_By_Admin.fulfilled
      .addCase(Signup_Teacher_By_Admin.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          state.Teacherslist.push(action.payload.data.newTeacher);
          state.AllTeacherlist.push(action.payload.data.newTeacher);
          state.loading = false;
        } else {
          toast.warning(action.payload, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: Signup_Teacher_By_Admin.rejected
      .addCase(Signup_Teacher_By_Admin.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: GetExistingTeacher_Availability.pending
      .addCase(GetExistingTeacher_Availability.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: GetExistingTeacher_Availability.fulfilled
      .addCase(GetExistingTeacher_Availability.fulfilled, (state, action) => {
        let slots = [];

        // Handle different response structures from the API
        if (action.payload) {
          // Case 1: Direct array of slots
          if (Array.isArray(action.payload) && action.payload[0]?.start) {
            slots = action.payload;
          }
          // Case 2: Teacher object with Availability.slots
          else if (action.payload.Availability?.slots) {
            slots = action.payload.Availability.slots;
          }
          // Case 3: Array of teachers, get first teacher's slots
          else if (Array.isArray(action.payload) && action.payload[0]?.Availability?.slots) {
            slots = action.payload[0].Availability.slots;
          }
          // Case 4: Legacy AvailableTimeSlots property
          else if (action.payload.AvailableTimeSlots) {
            slots = action.payload.AvailableTimeSlots;
          }
        }

        if (slots && slots.length > 0) {
          state.Teacher_Availabile_Slots = slots;
          state.loading = false;
        } else {
          state.Teacher_Availabile_Slots = [];
          const suppressToast = action.meta?.arg?.suppressToast;
          if (!suppressToast) {
            toast.error(t("store.teacher.noBookingsAvailable"), {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
            });
          }
        }
      })
      // extraReducers handler: GetExistingTeacher_Availability.rejected
      .addCase(GetExistingTeacher_Availability.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload?.message || t("store.teacher.failedToFetchAvailability"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: SearchTeacherbyUsername.pending
      .addCase(SearchTeacherbyUsername.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: SearchTeacherbyUsername.fulfilled
      .addCase(SearchTeacherbyUsername.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          state.Teacherslist = action.payload.data.founded_teacher;
          state.loading = false;
        } else {
          state.Teacherslist = action.payload;
          toast.warning(t("store.teacher.noTeacherFound"), {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: SearchTeacherbyUsername.rejected
      .addCase(SearchTeacherbyUsername.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: fetchTeacherBookings.pending
      .addCase(fetchTeacherBookings.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: fetchTeacherBookings.fulfilled
      .addCase(fetchTeacherBookings.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          state.Teachers_Bookings_Status = action.payload.data.TeacherBookings;
          state.loading = false;
        } else {
          state.Teacherslist = action.payload;
          toast.warning(t("store.teacher.noBookingsYet"), {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: fetchTeacherBookings.rejected
      .addCase(fetchTeacherBookings.rejected, (state) => {
        state.loading = false;
      })
      // extraReducers handler: changeTeacherPassword.pending
      .addCase(changeTeacherPassword.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: changeTeacherPassword.fulfilled
      .addCase(changeTeacherPassword.fulfilled, (state, action) => {
        state.loading = false;
        toast.success(t("store.teacher.passwordChanged"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: changeTeacherPassword.rejected
      .addCase(changeTeacherPassword.rejected, (state, action) => {
        state.loading = false;
        if (action.payload?.status === 503
          && action.payload?.code === 'PASSWORD_CHANGED_REAUTH_REQUIRED'
          && action.payload?.passwordChanged === true) {
          toast.info(t('teacherProfile.passwordChangedReauthRequired'), {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
          });
          return;
        }
        toast.error(action.payload?.message || t("store.teacher.failedToChangePassword"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: videoUpload.pending
      .addCase(videoUpload.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: videoUpload.fulfilled
      .addCase(videoUpload.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadedVideoFilename = action.payload;
        toast.success(t("store.teacher.videoUploaded"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: videoUpload.rejected
      .addCase(videoUpload.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.teacher.failedToUploadVideo"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: uploadToLocal.pending
      .addCase(uploadToLocal.pending, (state) => {
        state.fileLoading = true;
        state.fileError = null;
      })
      // extraReducers handler: uploadToLocal.fulfilled
      .addCase(uploadToLocal.fulfilled, (state, action) => {
        state.fileLoading = false;
        // Note: uploadToLocal should return the file with bookingId
        // For now, we'll just refresh the files for that booking
      })
      // extraReducers handler: uploadToLocal.rejected
      .addCase(uploadToLocal.rejected, (state, action) => {
        state.fileLoading = false;
        state.fileError = action.payload;
      })
      // extraReducers handler: listLocalFiles.pending
      .addCase(listLocalFiles.pending, (state, action) => {
        enterMaterialScope(state, action);
        const bookingId = action.meta?.arg?.bookingId || action.meta?.arg;
        state.materialListRequests[bookingId] = action.meta?.requestId;
        state.fileLoading = true;
        state.fileError = null;
      })
      // extraReducers handler: listLocalFiles.fulfilled
      .addCase(listLocalFiles.fulfilled, (state, action) => {
        const { bookingId, files } = action.payload;
        if (action.meta?.requestId && state.materialListRequests[bookingId] !== action.meta.requestId) return;
        delete state.materialListRequests[bookingId];
        state.fileLoading = Object.keys(state.materialListRequests).length > 0;
        state.localFiles[bookingId] = files;
      })
      // extraReducers handler: listLocalFiles.rejected
      .addCase(listLocalFiles.rejected, (state, action) => {
        const bookingId = action.meta?.arg?.bookingId || action.meta?.arg;
        if (action.meta?.requestId && state.materialListRequests[bookingId] !== action.meta.requestId) return;
        delete state.materialListRequests[bookingId];
        state.fileLoading = Object.keys(state.materialListRequests).length > 0;
        if (action.meta?.aborted) return;
        state.fileError = action.payload;
      })
      // Handle getMaterialsTeacher action
      .addCase(getMaterialsTeacher.pending, (state, action) => {
        state.materialsLoading = true;
        state.materialsError = null;
        state.materialsArchiveScope = action.meta?.arg?.archiveScope || 'active';
      })
      // extraReducers handler: getMaterialsTeacher.fulfilled
      .addCase(getMaterialsTeacher.fulfilled, (state, action) => {
        state.materialsLoading = false;
        const { data, pagination, archiveScope } = action.payload;

        // For first page or refresh, replace data
        if (pagination.currentPage === 1) {
          state.materialsData = data;
        } else {
          // For subsequent pages, append data
          state.materialsData = [...(state.materialsData || []), ...data];
        }

        state.materialsPagination = pagination;
        state.materialsArchiveScope = archiveScope || action.meta?.arg?.archiveScope || 'active';
      })
      // extraReducers handler: getMaterialsTeacher.rejected
      .addCase(getMaterialsTeacher.rejected, (state, action) => {
        state.materialsLoading = false;
        state.materialsError = action.payload;
      })
      // extraReducers handler: deleteLocalFile.pending
      .addCase(deleteLocalFile.pending, (state) => {
        state.fileLoading = true;
        state.fileError = null;
      })
      // extraReducers handler: deleteLocalFile.fulfilled
      .addCase(deleteLocalFile.fulfilled, (state, action) => {
        state.fileLoading = false;
        // Note: deleteLocalFile should return bookingId and fileName
        // For now, we'll just refresh the files for that booking
      })
      // extraReducers handler: deleteLocalFile.rejected
      .addCase(deleteLocalFile.rejected, (state, action) => {
        state.fileLoading = false;
        state.fileError = action.payload;
      })
      // extraReducers handler: downloadLocalFile.pending
      .addCase(downloadLocalFile.pending, (state, action) => {
        enterMaterialScope(state, action);
        state.materialDownloadRequests[action.meta?.requestId] = action.meta?.requestId;
        state.downloadLoading = true;
        state.downloadError = null;
      })
      // extraReducers handler: downloadLocalFile.fulfilled
      .addCase(downloadLocalFile.fulfilled, (state, action) => {
        if (action.meta?.requestId && !state.materialDownloadRequests[action.meta.requestId]) return;
        delete state.materialDownloadRequests[action.meta?.requestId];
        state.downloadLoading = Object.keys(state.materialDownloadRequests).length > 0;
        toast.success(t("store.teacher.fileDownloaded", { fileName: action.payload }), {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: downloadLocalFile.rejected
      .addCase(downloadLocalFile.rejected, (state, action) => {
        if (action.meta?.requestId && !state.materialDownloadRequests[action.meta.requestId]) return;
        delete state.materialDownloadRequests[action.meta?.requestId];
        state.downloadLoading = Object.keys(state.materialDownloadRequests).length > 0;
        if (action.meta?.aborted) return;
        state.downloadError = action.payload;
        toast.error(t("store.teacher.failedToDownloadFile"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: updateTeacherDetails.pending
      .addCase(updateTeacherDetails.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: updateTeacherDetails.fulfilled
      .addCase(updateTeacherDetails.fulfilled, (state, action) => {
        state.loading = false;
        const { teacherId, updatedTeacher } = action.payload;
        const updatedTeacherIndex = state.AllTeacherlist.findIndex(
          (teacher) => teacher._id === teacherId
        );

        if (updatedTeacherIndex !== -1) {
          const updatedTeacherData = {
            ...state.AllTeacherlist[updatedTeacherIndex],
            ...updatedTeacher,
          };
          state.AllTeacherlist = [
            ...state.AllTeacherlist.slice(0, updatedTeacherIndex),
            updatedTeacherData,
            ...state.AllTeacherlist.slice(updatedTeacherIndex + 1),
          ];
        }
        if (state.currentTeacher && state.currentTeacher._id === teacherId) {
          state.currentTeacher = { ...state.currentTeacher, ...updatedTeacher };
        }
        toast.success(t("store.teacher.detailsUpdated"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
      // extraReducers handler: updateTeacherDetails.rejected
      .addCase(updateTeacherDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(action.error.message || t("store.teacher.detailsUpdateFailed"), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
        });
      })
;
  },
});

export const { websocketConnected, websocketDisconnected, websocketMessageReceived, retireMaterialScope } = teacherSlice.actions;
export default teacherSlice.reducer;
